/* Horizontal "spin" carousel — scroll-snap row with arrow controls.
 * Renders children in a horizontally scrolling track; arrow buttons
 * scroll by the width of one item (clientWidth/itemsPerView).
 */
import React, { useRef, useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export default function SpinCarousel({ children, ariaLabel, testid, itemsPerScroll = 1, gap = 16 }) {
  const trackRef = useRef(null);
  const [showLeft, setShowLeft]   = useState(false);
  const [showRight, setShowRight] = useState(false);

  const update = () => {
    const el = trackRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 8);
    setShowRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  useEffect(() => {
    update();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => { el.removeEventListener('scroll', update); ro.disconnect(); };
  }, [children]);

  const step = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const child = el.querySelector('[data-spin-item]');
    const width = child ? child.getBoundingClientRect().width + gap : el.clientWidth * 0.6;
    el.scrollBy({ left: dir * width * itemsPerScroll, behavior: 'smooth' });
  };

  return (
    <div className="relative" data-testid={testid} aria-label={ariaLabel}>
      <button
        type="button"
        onClick={() => step(-1)}
        aria-label="Scroll left"
        data-testid={`${testid}-prev`}
        className={`absolute -left-3 top-1/2 z-20 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-panel-active transition-opacity hover:bg-primary hover:text-primary-foreground sm:flex ${showLeft ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => step(1)}
        aria-label="Scroll right"
        data-testid={`${testid}-next`}
        className={`absolute -right-3 top-1/2 z-20 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-panel-active transition-opacity hover:bg-primary hover:text-primary-foreground sm:flex ${showRight ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
      <div
        ref={trackRef}
        className="no-scrollbar flex snap-x snap-mandatory overflow-x-auto scroll-smooth px-0 pb-8 pt-3 sm:px-2 sm:pb-16 sm:pt-6"
        style={{ gap: `${gap}px` }}
      >
        {React.Children.map(children, (c, i) => (
          <div data-spin-item key={i} className="snap-start">
            {c}
          </div>
        ))}
      </div>
    </div>
  );
}
